/**
 * Beach Salvage — haul stranded logs off the sand and into the boom.
 *
 * The loop per log is two beats: heave it off the beach (lean on the line
 * until the sand lets go and it slides into deep water), then tow it out to the
 * floating collection boom, which claims it on arrival. When the beach is clear
 * a fresh set washes up.
 *
 * The tug gets the same seabed as the logs, tuned so it merely runs out of
 * water at the shore rather than climbing the sand — back off and it floats
 * free again.
 */

import { Vector3 } from 'three/webgpu'
import type { Group } from 'three/webgpu'
import { Beach, bedHeight } from '../world/Beach.ts'
import { Log } from '../entities/Log.ts'
import type { GameMode, ModeContext } from './GameMode.ts'
import type { WakeSource } from '../world/Ocean.ts'

/** A line can be thrown a long way to a log up the beach — you don't need to
 *  risk the shallows to reach one now that grounding is a crash. */
const HOOK_RANGE = 150
/** The tug stops at the shore but isn't glued there — reverse pulls it off. */
const TUG_GROUND_FRICTION = 5.5e5
// Meaningfully bottoming out — the hull is bearing on the sand. Set below the
// ~0.46 grip where the tug plateaus stuck in the shallows (from that point
// even full reverse barely moves her), so "stuck and can't back off" is caught.
const AGROUND_GRIP = 0.4
// Driving onto the sand this hard counts even while still moving — so charging
// the beach at speed crashes promptly, before the tug plows halfway up it,
// rather than only once it has ground to a halt.
const HARD_AGROUND_GRIP = 0.6
/** Below this (m/s, ~1 kn) the tug counts as not making way. */
const STUCK_SPEED = 0.5
/** Aground this long is a crash; a quick graze in passing is not. */
const CRASH_SECONDS = 0.4
/** Deep water the tug is refloated to after running aground. */
const REFLOAT_Z = -95

const _tugTow = new Vector3()
const _logTow = new Vector3()

export class BeachMode implements GameMode {
  readonly worldGroup: Group
  readonly tugSpawn: { position: Vector3; heading: number }
  private readonly hookRange: number

  private readonly beach = new Beach()
  private logs: Log[] = []
  private delivered: boolean[] = []
  private currentIndex = 0
  private salvaged = 0
  private spawnSeed = 1
  private groundedTime = 0
  private refloating = false
  private refloatTimer = 0
  private respawnTimer = 0

  constructor(private readonly ctx: ModeContext) {
    this.worldGroup = this.beach.group
    // Deep-draft vessels need a longer salvage line to stay afloat offshore.
    const deepShip = ctx.tug.config.draft > 9
    const offshoreClearance = (ctx.tug.config.draft + 3) / .06
    this.hookRange = deepShip ? offshoreClearance + 30 : HOOK_RANGE
    // Large ships start stern-to-shore, with room for their full draft and
    // the aft tow point facing the first log. The tug keeps its original start.
    this.tugSpawn = deepShip
      ? { position: new Vector3(-70, 0, -ctx.tug.config.length * .5 - offshoreClearance), heading: Math.PI }
      : ctx.tug.config.length > 50
      ? { position: new Vector3(-70, 0, ctx.tug.kind === 'cruise-ship' ? -240 : -170), heading: Math.PI }
      : this.beach.tugSpawn
  }

  start(): void {
    // The tug now feels the bottom — it grounds gently in the shallows.
    const tug = this.ctx.tug
    tug.bedSampler = tug.config.length > 50 ? (x, z) => {
      // Ground on the shallowest part under this long hull, not just amidships.
      const reach = tug.config.length * .45
      const dx = Math.sin(tug.yaw) * reach
      const dz = Math.cos(tug.yaw) * reach
      return Math.max(bedHeight(x, z), bedHeight(x + dx, z + dz), bedHeight(x - dx, z - dz))
    } : bedHeight
    tug.config.groundFriction = TUG_GROUND_FRICTION * (tug.config.mass / 280_000)
    this.spawnLogs()
    this.ctx.hud.announce('Salvage the logs — pull each one off the beach', 6)
  }

  private spawnLogs(): void {
    for (const log of this.logs) this.ctx.scene.remove(log.group)
    this.logs = this.beach.logSpawns.map((spawn, i) => {
      const log = new Log(this.spawnSeed * 31 + i)
      log.position.copy(spawn.position)
      log.position.y = bedHeight(spawn.position.x, spawn.position.z) + log.config.draft
      log.yaw = spawn.heading
      this.ctx.scene.add(log.group)
      return log
    })
    this.delivered = this.logs.map(() => false)
    this.currentIndex = 0
    this.spawnSeed++
  }

  private get currentLog(): Log | null {
    return this.currentIndex < this.logs.length ? this.logs[this.currentIndex] : null
  }

  fixedStep(dt: number, elapsed: number): void {
    for (const log of this.logs) log.step(dt, elapsed)

    this.checkAground(dt)

    // Auto-deliver: an afloat log towed into the boom is claimed.
    const { hawser, hud } = this.ctx
    const log = this.currentLog
    if (hawser.connected && log && log.afloat) {
      const b = this.beach.boom
      if (Math.hypot(log.position.x - b.position.x, log.position.z - b.position.z) < b.radius) {
        hawser.release()
        this.delivered[this.currentIndex] = true
        this.salvaged++
        this.advance()
        hud.announce('Log boomed! Nice hauling', 4)
      }
    }
  }

  /**
   * Drive the tug onto the sand and you've crashed. It's not just a telling-off:
   * hard aground there's no water for the propeller, so she can't back herself
   * off — we drop the tow and refloat her in deep water. Brief grazes are
   * forgiven so nosing up to a log near the shore doesn't count.
   */
  private checkAground(dt: number): void {
    if (this.refloating) return
    const t = this.ctx.tug
    // Stuck = bottoming out AND either dead in the water (can't power off) or
    // so hard aground the prop is out of water. A graze taken at speed, or a
    // deliberate reverse that's actually working, keeps her moving and doesn't
    // count — so escaping under your own power is always rewarded.
    const stuck = t.grounding > AGROUND_GRIP && (t.speed < STUCK_SPEED || t.grounding > HARD_AGROUND_GRIP)
    if (!stuck) {
      this.groundedTime = 0
      return
    }
    this.groundedTime += dt
    if (this.groundedTime < CRASH_SECONDS) return

    this.refloating = true
    this.groundedTime = 0
    this.ctx.hawser.release()
    this.ctx.engineSound.crash()
    this.ctx.hud.announce('CRASHED — ran aground! Refloating…', 4)

    t.position.set(t.position.x, 0, t.config.length <= 50 ? REFLOAT_Z : this.tugSpawn.position.z)
    t.velocity.set(0, 0, 0)
    t.yawRate = 0
    t.pitch = 0
    t.roll = 0
    t.pitchRate = 0
    t.rollRate = 0
    // A short grace period so the refloat itself can't immediately re-trigger.
    this.refloatTimer = window.setTimeout(() => {
      this.refloating = false
    }, 1200)
  }

  private advance(): void {
    do {
      this.currentIndex++
    } while (this.currentIndex < this.logs.length && this.delivered[this.currentIndex])
    if (this.currentIndex >= this.logs.length) {
      this.ctx.hud.announce('Beach cleared! Fresh logs washing in…', 5)
      this.respawnTimer = window.setTimeout(() => this.spawnLogs(), 5000)
    }
  }

  /** A landing craft's ramp rests on the same sand the logs do. */
  groundHeight(x: number, z: number): number {
    return bedHeight(x, z)
  }

  toggleHook(): void {
    const { tug, hawser, hud } = this.ctx
    if (hawser.connected) {
      hawser.release()
      hud.announce('Line let go', 2)
      return
    }
    const log = this.currentLog
    if (!log) return
    tug.towPointWorld(_tugTow)
    log.towPointWorld(_logTow)
    if (Math.hypot(_tugTow.x - _logTow.x, _tugTow.z - _logTow.z) > this.hookRange) {
      hud.announce('Get closer to pass a line to the log', 2.5)
      return
    }
    hawser.attach(
      { vessel: tug, bodyPoint: tug.attachments.towPoint },
      { vessel: log, bodyPoint: log.attachments.towPoint },
    )
    hud.announce('Made fast — haul it off the sand!', 3)
  }

  frame(elapsed: number): void {
    const { tug, hawser, hud, waypoint, chase } = this.ctx
    for (const log of this.logs) log.syncVisual()
    this.beach.update(elapsed)

    const log = this.currentLog
    // The arrow only guides you home once a log is on the wire — finding the
    // logs strewn on the beach is the fun part, so no arrow points them out.
    const objective = hawser.connected && log ? this.beach.boom.position : null
    const distance = waypoint.update(tug.position, objective, chase.camera)

    if (log && !hawser.connected) {
      tug.towPointWorld(_tugTow)
      log.towPointWorld(_logTow)
      hud.setHookState(Math.hypot(_tugTow.x - _logTow.x, _tugTow.z - _logTow.z) <= this.hookRange, false)
    } else {
      hud.setHookState(false, hawser.connected)
    }

    const remaining = this.logs.length - this.salvaged
    const text = log
      ? hawser.connected
        ? log.afloat
          ? 'Tow the log to the boom' + this.away(distance)
          : 'Haul the log off the beach!'
        : 'Hook the stranded log' + this.away(distance)
      : 'Beach cleared'
    hud.setMission(text, `${remaining} log${remaining === 1 ? '' : 's'} to go`, this.salvaged)
  }

  private away(distanceMetres: number): string {
    if (distanceMetres < 30) return ''
    return distanceMetres >= 950
      ? ` · ${(distanceMetres / 1000).toFixed(1)} km away`
      : ` · ${Math.round(distanceMetres / 10) * 10} m away`
  }

  towWake(): WakeSource | null {
    // A log is dead cargo — it doesn't throw a wake of its own, and a foam
    // streak trailing it only muddies the tow. The tug's wake is enough.
    return null
  }

  dispose(): void {
    clearTimeout(this.refloatTimer)
    clearTimeout(this.respawnTimer)
    for (const log of this.logs) this.ctx.scene.remove(log.group)
    this.ctx.scene.remove(this.worldGroup)
    // Hand the tug back its deep-water manners; harbour mode has no seabed.
    this.ctx.tug.bedSampler = null
    this.ctx.tug.config.groundFriction = 0
  }
}
